import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { TablesInsert, TablesUpdate } from '@/types/database';

export type ClientFilters = {
  search?: string;
  status?: string;
  package?: string;
};

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: ['clients', filters],
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      let query = supabase.from('clients').select('*').order('name');

      if (filters.search) query = query.ilike('name', `%${filters.search}%`);
      if (filters.status) query = query.eq('status', filters.status as never);
      if (filters.package) query = query.eq('package', filters.package);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useClientMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['clients'] });

  const create = useMutation({
    mutationFn: async (payload: TablesInsert<'clients'>) => {
      const { data, error } = await supabase.from('clients').insert(payload).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Cliente criado com sucesso');
      invalidate();
    },
    onError: () => toast.error('Erro ao criar cliente'),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: TablesUpdate<'clients'> & { id: string }) => {
      const { data, error } = await supabase
        .from('clients')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Cliente atualizado');
      invalidate();
    },
    onError: () => toast.error('Erro ao atualizar cliente'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Cliente removido');
      invalidate();
    },
    onError: () => toast.error('Erro ao remover cliente'),
  });

  return { create, update, remove };
}
